import { extendTheme } from '@chakra-ui/react';
import colors from './foundations/colors';
import Card from './components/card';
import Button from './components/button';
import Table from './components/table';
import Modal from './components/modal';
import Input from './components/input';
import Select from './components/select';
import AlertDialog from './components/alert-dialog';
import Tabs from './components/tabs';
import Textarea from './components/textarea';
import Checkbox from './components/checkbox';
import Popover from './components/popover';
import Radio from './components/radio';
import Progress from './components/progress';

const theme = extendTheme({
  // 1. Colors
  colors,
  semanticTokens: {
    colors: {
      'text.primary': { default: '#172033', _dark: '#F3F4F6' },
      'text.secondary': { default: '#5B6575', _dark: '#D1D5DB' },
      'text.muted': { default: '#5B6575', _dark: '#D1D5DB' },
      'ink.primary': { default: '#172033', _dark: '#F3F4F6' },
      'ink.secondary': { default: '#5B6575', _dark: '#D1D5DB' },
    },
  },

  // 2. Typography - Larger, more professional
  fonts: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif',
    heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif',
    mono: '"SF Mono", "Consolas", "Liberation Mono", Menlo, monospace',
  },
  fontSizes: {
    xs: "0.75rem",   // 12px
    sm: "0.875rem",   // 14px
    md: "1rem",   // 16px
    lg: "1.1875rem",   // 19px
    xl: "1.375rem",    // 22px
    "2xl": "1.625rem", // 26px
    "3xl": "2rem",     // 32px
    "4xl": "2.5rem",   // 40px
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeights: {
    normal: 1.6,
    short: 1.4,
    shorter: 1.2,
    tall: 1.8,
  },

  // 3. Responsive Breakpoints
  breakpoints: {
    sm: '30em',   // 480px
    md: '48em',   // 768px
    lg: '64em',   // 1024px
    xl: '80em',   // 1280px
    '2xl': '96em', // 1536px
  },

  // 4. Spacing Scale
  space: {
    px: '1px',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem',
  },

  // 5. Border Radius - Slightly sharper for professional look
  radii: {
    none: '0',
    sm: '0.1875rem',  // 3px
    base: '0.3125rem', // 5px
    md: '0.625rem',   // 10px
    lg: '0.75rem',    // 12px
    xl: '1rem',    // 16px
    '2xl': '1.25rem', // 20px
    '3xl': '1.5rem',   // 24px
    full: '9999px',
  },

  // 6. Quiet elevation; resting content uses borders and spacing.
  shadows: {
    sm: '0 1px 2px rgba(23, 32, 51, 0.04)',
    base: '0 2px 6px rgba(23, 32, 51, 0.06)',
    md: '0 4px 12px rgba(23, 32, 51, 0.08)',
    lg: '0 8px 24px rgba(23, 32, 51, 0.10)',
    xl: '0 12px 32px rgba(23, 32, 51, 0.12)',
    '2xl': '0 16px 40px rgba(23, 32, 51, 0.14)',
    outline: '0 0 0 3px rgba(37, 99, 235, 0.25)',
    focus: '0 0 0 3px rgba(37, 99, 235, 0.25)',
    focus_admin: '0 0 0 3px rgba(37, 99, 235, 0.25)',
    none: 'none',
    chat: 'none',
    chatHover: '0 1px 3px rgba(23, 32, 51, 0.08)',
    'dark-sm': '0 1px 3px rgba(0, 0, 0, 0.16)',
    'dark-md': '0 4px 12px rgba(0, 0, 0, 0.20)',
    'dark-lg': '0 8px 24px rgba(0, 0, 0, 0.24)',

  },

  // 7. Z-Index Scale
  zIndices: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },

  // 6. Component Styles
  components: {
    Card,
    Button,
    Table,
    Modal,
    Input,
    Select,
    AlertDialog,
    Tabs,
    Textarea,
    Checkbox,
    Popover,
    Radio,
    Progress,
  },

  // 8. Global Styles
  styles: {
    global: (props) => ({
      '*': {
        boxSizing: 'border-box',
      },
      '*, *::before, *::after': {
        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
      },
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'surface.canvas',
        color: props.colorMode === 'dark' ? 'text.inverted' : 'ink.primary',
        fontFeatureSettings: '"kern" 1',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        scrollBehavior: 'smooth',
        lineHeight: '1.6',
        letterSpacing: '-0.01em',
      },
      html: {
        scrollBehavior: 'smooth',
      },
      '#root': {
        minHeight: '100vh',
        isolation: 'isolate',
      },
      // Focus styles
      '*:focus-visible': {
        outline: '3px solid #2563EB',
        outlineOffset: '2px',
        boxShadow: 'none',
      },

    }),
  },

  // Color mode remains explicit; public Chatbox follows its own system preference.
  // 9. Color Mode Configuration
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
});

export default theme;
